import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { ButtonComponent } from "shared/components";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "config/firebase";
import {
  modalVariants,
  childVariants,
  childVariantsDown,
  cld,
} from "shared/utils";
import "../styles/EditCertificatesModal.css";
const resolveImg = (val, width) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  return cld(val, width);
};
const EditCertificatesModal = ({
  isOpen,
  onClose,
  certificateData,
  onSave,
  isNewCertificate,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    image: "",
    title: "",
    platform: "",
    logo: "",
    issuedOn: "",
    credentialId: "",
    link: "",
    description: "",
    modelListItems: "",
  });
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isAddingTopic, setIsAddingTopic] = useState(false);
  const [editingTopicIndex, setEditingTopicIndex] = useState(null);
  const [newTopicText, setNewTopicText] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (isNewCertificate || !certificateData) {
        setFormData({
          id: "",
          image: "",
          title: "",
          platform: "",
          logo: "",
          issuedOn: "",
          credentialId: "",
          link: "",
          description: "",
          modelListItems: "",
        });
      } else {
        setFormData({ ...certificateData });
      }
      setErrors({});
      setImageFile(null);
      setLogoFile(null);
    }
  }, [isOpen, certificateData, isNewCertificate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.platform.trim()) newErrors.platform = "Platform is required";
    if (!formData.issuedOn.trim())
      newErrors.issuedOn = "Issue date is required";
    if (!formData.credentialId.trim())
      newErrors.credentialId = "Credential ID is required";
    if (!formData.link.trim()) newErrors.link = "Link is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setUploading(true);
    try {
      let imageUrl = formData.image;
      let logoUrl = formData.logo;

      // Upload certificate image if new file selected
      if (imageFile) {
        const imageRef = ref(
          storage,
          `portfolio/Certificates/${Date.now()}_${imageFile.name}`,
        );
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Upload logo if new file selected
      if (logoFile) {
        const logoRef = ref(
          storage,
          `portfolio/Logo/${Date.now()}_${logoFile.name}`,
        );
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
      }

      const certificateToSave = {
        ...formData,
        id: formData.id || `cert-${Date.now()}`,
        image: imageUrl,
        logo: logoUrl,
      };

      onSave(certificateToSave);
      setUploading(false);
      onClose();
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload images. Please try again.");
      setUploading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, image: previewUrl });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, logo: previewUrl });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  const getTopics = () => {
    return formData.modelListItems
      ? formData.modelListItems
          .split("|")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];
  };

  const updateTopicsString = (topicsArray) => {
    setFormData({ ...formData, modelListItems: topicsArray.join(" | ") });
  };

  const handleAddTopic = () => {
    if (!newTopicText.trim()) return;
    const topics = getTopics();
    topics.push(newTopicText.trim());
    updateTopicsString(topics);
    setNewTopicText("");
    setIsAddingTopic(false);
  };

  const handleDeleteTopic = (index) => {
    const topics = getTopics();
    topics.splice(index, 1);
    updateTopicsString(topics);
    if (editingTopicIndex === index) setEditingTopicIndex(null);
  };

  const handleEditTopic = (index) => {
    const topics = getTopics();
    setEditingTopicIndex(index);
    setNewTopicText(topics[index] || "");
    setIsAddingTopic(false);
  };

  const handleSaveEditTopic = () => {
    if (editingTopicIndex === null) return;
    const topics = getTopics();
    if (!newTopicText.trim()) return;
    topics[editingTopicIndex] = newTopicText.trim();
    updateTopicsString(topics);
    setEditingTopicIndex(null);
    setNewTopicText("");
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "auto",
              width: "90%",
            }}
          >
            <i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
            ></i>

            <h3 className="preview-modal-title">
              {isNewCertificate ? "Add New Certificate" : "Edit Certificate"}
            </h3>

            <div className="preview-modal-container">
              {/* Left Side: Details */}
              <motion.div
                style={{ flex: 1 }}
                variants={childVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Title Section */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    position: "relative",
                    marginBottom: "0.5rem",
                  }}
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Certificate Title *
                  </label>
                  <input
                    className="glass__card"
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Python Core Certification"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      marginTop: "0.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                    }}
                  />
                  {errors.title && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.title}
                    </span>
                  )}
                </motion.div>

                {/* Description Section */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    position: "relative",
                    marginBottom: "0.5rem",
                  }}
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Description *
                  </label>
                  <textarea
                    className="glass__card"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the certification..."
                    style={{
                      width: "100%",
                      height: "65px",
                      marginTop: "0.5rem",
                      padding: "0.75rem 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                  />
                  {errors.description && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.description}
                    </span>
                  )}
                </motion.div>

                {/* Two Column Row: Credential ID & Link */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ display: "flex", gap: "1rem" }}
                >
                  {/* Credential ID */}
                  <motion.div
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    style={{
                      position: "relative",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <label
                      className="preview-modal-subtitle"
                      style={{
                        fontWeight: "600",
                        fontSize: "small",
                      }}
                    >
                      Credential ID *
                    </label>
                    <input
                      className="glass__card"
                      type="text"
                      name="credentialId"
                      value={formData.credentialId}
                      onChange={handleInputChange}
                      placeholder="e.g., CT-QQBBKTN9"
                      style={{
                        width: "100%",
                        height: "2.5rem",
                        marginTop: "0.5rem",
                        padding: "0 1rem",
                        border: "2px solid var(--title-color)",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        backgroundColor: "var(--body-color)",
                        color: "var(--border-color)",
                        outline: "none",
                      }}
                    />
                    {errors.credentialId && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontSize: "0.7rem",
                          marginTop: "0.25rem",
                          display: "block",
                        }}
                      >
                        {errors.credentialId}
                      </span>
                    )}
                  </motion.div>

                  {/* Verification Link */}
                  <motion.div
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    style={{
                      position: "relative",
                    }}
                  >
                    <label
                      className="preview-modal-subtitle"
                      style={{
                        fontWeight: "600",
                        fontSize: "small",
                      }}
                    >
                      Verification Link *
                    </label>
                    <input
                      className="glass__card"
                      type="url"
                      name="link"
                      value={formData.link}
                      onChange={handleInputChange}
                      placeholder="e.g., https://www.sololearn.com/certificates/..."
                      style={{
                        width: "100%",
                        height: "2.5rem",
                        marginTop: "0.5rem",
                        padding: "0 1rem",
                        border: "2px solid var(--title-color)",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        backgroundColor: "var(--body-color)",
                        color: "var(--border-color)",
                        outline: "none",
                      }}
                    />
                    {errors.link && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontSize: "0.7rem",
                          marginTop: "0.25rem",
                          display: "block",
                        }}
                      >
                        {errors.link}
                      </span>
                    )}
                  </motion.div>
                </motion.div>

                {/* Two Column Row: Platform & Issue Date */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ display: "flex", gap: "1rem" }}
                >
                  {/* Platform */}
                  <motion.div
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    style={{
                      position: "relative",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <label
                      className="preview-modal-subtitle"
                      style={{
                        fontWeight: "600",
                        fontSize: "small",
                      }}
                    >
                      Platform *
                    </label>
                    <input
                      className="glass__card"
                      type="text"
                      name="platform"
                      value={formData.platform}
                      onChange={handleInputChange}
                      placeholder="e.g., Coursera, Udemy, Google"
                      style={{
                        width: "100%",
                        height: "2.5rem",
                        marginTop: "0.5rem",
                        padding: "0 1rem",
                        border: "2px solid var(--title-color)",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        backgroundColor: "var(--body-color)",
                        color: "var(--border-color)",
                        outline: "none",
                      }}
                    />
                    {errors.platform && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontSize: "0.7rem",
                          marginTop: "0.25rem",
                          display: "block",
                        }}
                      >
                        {errors.platform}
                      </span>
                    )}
                  </motion.div>

                  {/* Issue Date */}
                  <motion.div
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    style={{
                      position: "relative",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <label
                      className="preview-modal-subtitle"
                      style={{
                        fontWeight: "600",
                        fontSize: "small",
                      }}
                    >
                      Issue Date *
                    </label>
                    <input
                      className="glass__card"
                      type="text"
                      name="issuedOn"
                      value={formData.issuedOn}
                      onChange={handleInputChange}
                      placeholder="e.g., Jan 2024 or 2024-01-15"
                      style={{
                        width: "100%",
                        height: "2.5rem",
                        marginTop: "0.5rem",
                        padding: "0 1rem",
                        border: "2px solid var(--title-color)",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        backgroundColor: "var(--body-color)",
                        color: "var(--border-color)",
                        outline: "none",
                      }}
                    />
                    {errors.issuedOn && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontSize: "0.7rem",
                          marginTop: "0.25rem",
                          display: "block",
                        }}
                      >
                        {errors.issuedOn}
                      </span>
                    )}
                  </motion.div>
                </motion.div>

                {/* Key Topics Section */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      display: "block",
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Key Topics
                  </label>
                  <ul
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      padding: 0,
                      marginTop: "0.5rem",
                      marginBottom: "0.5rem",
                      listStyle: "none",
                    }}
                  >
                    {getTopics().length > 0 &&
                      getTopics().map((topic, index) => (
                        <motion.div
                          className="glass__card"
                          variants={childVariants}
                          initial="hidden"
                          animate="visible"
                          key={index}
                          style={{
                            border: "2px solid var(--title-color)",
                            borderRadius: "0.5rem",
                            padding: "0.5rem 1rem",
                            height: "2.5rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.5rem",
                            position: "relative",
                            backgroundColor:
                              "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: "600",
                              color: "var(--title-color)",
                              margin: 0,
                              wordBreak: "break-word",
                            }}
                          >
                            {topic}
                          </p>
                          <div
                            style={{
                              top: "0.35rem",
                              right: "0.35rem",
                              display: "flex",
                              gap: "0.25rem",
                            }}
                          >
                            <motion.button
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleEditTopic(index)}
                              className="glass__button"
                              style={{
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "var(--body-color)",
                                padding: 0,
                                background: "var(--title-color)",
                              }}
                              title="Edit topic"
                            >
                              <i
                                className="bx bx-pencil"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteTopic(index)}
                              className="glass__button"
                              style={{
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "var(--body-color)",
                                padding: 0,
                                background: "var(--title-color)",
                              }}
                              title="Delete topic"
                            >
                              <i
                                className="bx bx-trash"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </motion.button>
                          </div>
                        </motion.div>
                      ))}

                    {/* Add New Topic Card */}
                    {getTopics().length < 4 && (
                      <motion.div
                        className="glass__card"
                        variants={childVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setIsAddingTopic(true)}
                        style={{
                          border: "var(--border-color)",
                          borderRadius: "0.5rem",
                          padding: "0.5rem 1rem",
                          height: "2.5rem",
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className="bx bx-plus"
                          style={{
                            fontSize: "1.5rem",
                            color: "var(--title-color)",
                            marginBottom: "0.15rem",
                          }}
                        ></i>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "500",
                            color: "var(--title-color)",
                          }}
                        >
                          Add Topic
                        </span>
                      </motion.div>
                    )}
                  </ul>

                  {/* Edit/Add Form Below All Cards */}
                  {(isAddingTopic || editingTopicIndex !== null) && (
                    <motion.div
                      variants={childVariantsDown}
                      initial="hidden"
                      animate="visible"
                      style={{ marginTop: "1rem", marginBottom: "1rem" }}
                    >
                      <label
                        className="preview-modal-subtitle"
                        style={{
                          marginBottom: "0.5rem",
                          fontWeight: "600",
                          fontSize: "small",
                          marginTop: "1rem",
                        }}
                      >
                        {editingTopicIndex !== null
                          ? "Edit Topic"
                          : "Add New Topic"}
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          marginTop: "0.5rem",
                        }}
                      >
                        <input
                          className="glass__card"
                          type="text"
                          value={newTopicText}
                          onChange={(e) => setNewTopicText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              editingTopicIndex !== null
                                ? handleSaveEditTopic()
                                : handleAddTopic();
                            }
                          }}
                          placeholder="e.g., Data Types, Control Structures"
                          style={{
                            flex: 1,
                            padding: "0.5rem",
                            border: "2px solid var(--title-color)",
                            borderRadius: "0.5rem",
                            fontSize: "0.875rem",
                            backgroundColor: "var(--body-color)",
                            color: "var(--border-color)",
                            outline: "none",
                          }}
                        />
                        {editingTopicIndex !== null ? (
                          <>
                            <ButtonComponent
                              buttonTitle="Save"
                              buttonText="Save"
                              onClick={handleSaveEditTopic}
                            />
                            <ButtonComponent
                              buttonTitle="Cancel"
                              buttonText="Cancel"
                              onClick={() => {
                                setEditingTopicIndex(null);
                                setNewTopicText("");
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <ButtonComponent
                              buttonTitle="Add"
                              buttonText="Add"
                              onClick={handleAddTopic}
                              disabled={!newTopicText.trim()}
                            />
                            <ButtonComponent
                              buttonTitle="Cancel"
                              buttonText="Cancel"
                              onClick={() => {
                                setIsAddingTopic(false);
                                setNewTopicText("");
                              }}
                            />
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>

              {/* Right Side: Images & Platform Info */}
              <motion.div
                className="image-upload-section"
                variants={childVariants}
                initial="hidden"
                animate="visible"
                style={{
                  flex: "0.5 1 0%",
                  marginTop: "1.5rem",
                  gap: "0.5rem",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Certificate Image */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ position: "relative" }}
                >
                  {/* Certificate Image */}
                  <label
                    className="glass__card"
                    style={{
                      borderRadius: "0.5rem",
                      overflow: "hidden",
                      width: "100%",
                      aspectRatio: "4.75 / 3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                      cursor: "pointer",
                      position: "relative",
                      padding: "0.5rem",
                      border: "2px solid var(--title-color)",
                    }}
                  >
                    {formData.image ? (
                      <>
                        <img
                          src={resolveImg(formData.image, 400)}
                          alt="Certificate Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "0.5rem",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "0.5rem",
                            right: "0.5rem",
                            width: "2.5rem",
                            height: "2.5rem",
                            borderRadius: "0.375rem",
                            backgroundColor: "var(--title-color)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          }}
                        >
                          <i
                            className="bx bx-upload"
                            style={{
                              fontSize: "1.5rem",
                              color: "var(--container-color)",
                            }}
                          ></i>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--text-color)",
                          textAlign: "center",
                          padding: "1rem",
                        }}
                      >
                        <i
                          className="bx bx-upload"
                          style={{ fontSize: "2rem", marginBottom: "0.5rem" }}
                        ></i>
                        <span style={{ fontSize: "0.75rem" }}>
                          Upload Certificate Image
                        </span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                  </label>

                  {/* Certificate Image URL */}
                  <motion.div
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    style={{
                      position: "relative",
                      marginTop: "1rem",
                    }}
                  >
                    <label
                      className="preview-modal-subtitle"
                      style={{
                        fontWeight: "600",
                        fontSize: "small",
                      }}
                    >
                      Image URL
                    </label>
                    <input
                      className="glass__card"
                      type="text"
                      value={formData.image}
                      onChange={(e) =>
                        setFormData({ ...formData, image: e.target.value })
                      }
                      placeholder="e.g., https://example.com/image.jpg"
                      style={{
                        width: "100%",
                        height: "2.5rem",
                        marginTop: "0.5rem",
                        padding: "0 1rem",
                        border: "2px solid var(--title-color)",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        backgroundColor: "var(--body-color)",
                        color: "var(--border-color)",
                        outline: "none",
                      }}
                      onClick={() =>
                        formData.image &&
                        navigator.clipboard.writeText(formData.image)
                      }
                      title={
                        formData.image ? "Click to copy URL" : "Paste URL here"
                      }
                    />
                  </motion.div>
                </motion.div>

                {/* Platform Logo */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ position: "relative", marginBottom: "1rem" }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "start",
                      textAlign: "left",
                    }}
                  >
                    <motion.div
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      style={{ width: "100%" }}
                    >
                      <label
                        className="glass__card"
                        style={{
                          borderRadius: "0.5rem",
                          overflow: "hidden",
                          width: "100%",
                          aspectRatio: "8.5 / 3",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor:
                            "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                          cursor: "pointer",
                          position: "relative",
                          padding: "0.5rem",
                          border: "2px solid var(--title-color)",
                        }}
                      >
                        {formData.logo ? (
                          <>
                            <img
                              src={resolveImg(formData.logo, 300)}
                              alt="Logo Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: "0.15rem",
                                right: "0.15rem",
                                width: "1.25rem",
                                height: "1.25rem",
                                borderRadius: "0.2rem",
                                backgroundColor: "var(--title-color)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
                              }}
                            >
                              <i
                                className="bx bx-upload"
                                style={{
                                  fontSize: "0.7rem",
                                  color: "var(--container-color)",
                                }}
                              ></i>
                            </div>
                          </>
                        ) : (
                          <i
                            className="bx bx-upload"
                            style={{
                              fontSize: "1.25rem",
                              color: "var(--text-color)",
                            }}
                          ></i>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          style={{ display: "none" }}
                        />
                      </label>
                    </motion.div>
                    <motion.div
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      style={{ width: "100%" }}
                    >
                      <label
                        className="preview-modal-subtitle"
                        style={{
                          fontWeight: "600",
                          fontSize: "small",
                        }}
                      >
                        Platform Logo URL
                      </label>
                      <input
                        className="glass__card"
                        type="text"
                        value={formData.logo}
                        onChange={(e) =>
                          setFormData({ ...formData, logo: e.target.value })
                        }
                        placeholder="e.g., https://example.com/logo.png"
                        style={{
                          width: "100%",
                          height: "2.5rem",
                          padding: "0 1rem",
                          marginTop: "0.5rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                        }}
                        onClick={() =>
                          formData.logo &&
                          navigator.clipboard.writeText(formData.logo)
                        }
                        title={
                          formData.logo ? "Click to copy URL" : "Paste URL here"
                        }
                      />
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Action Buttons */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <ButtonComponent
                buttonTitle="Cancel"
                buttonText="Cancel"
                onClick={onClose}
              />
              <ButtonComponent
                buttonTitle={
                  isNewCertificate ? "Add Certificate" : "Save Changes"
                }
                buttonText={
                  uploading
                    ? "Uploading..."
                    : isNewCertificate
                      ? "Add Certificate"
                      : "Save Changes"
                }
                onClick={handleSave}
                disabled={uploading}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditCertificatesModal;
